import { http } from '@/shared/api/api-client';

import type { SearchHit } from '../types/global-search.types';

/**
 * Global search through the Admin API's own list endpoints, so permissions and
 * country scope are enforced exactly as on each list page. A few results per
 * kind; the list page shows the rest.
 */

const LIMIT = 5;

interface AdminPage<T> {
  items: T[];
}

export async function searchCustomers(
  term: string,
  countryId: number | undefined,
  signal?: AbortSignal,
): Promise<SearchHit[]> {
  const page = await http.get<
    AdminPage<{ id: number; name: string | null; msisdn: string | null; email: string | null }>
  >('/admin/customers', { params: { search: term, countryId, page: 1, pageSize: LIMIT }, signal });
  return page.items.map((c) => ({
    kind: 'customer',
    id: c.id,
    title: c.name ?? `Customer #${c.id}`,
    detail: [c.msisdn, c.email].filter(Boolean).join(' · ') || null,
    to: `/customers/${c.id}`,
  }));
}

/** Orders in the last 30 days (the list's default window) by id, receipt, reference, phone or name. */
export async function searchOrders(
  term: string,
  countryId: number | undefined,
  signal?: AbortSignal,
): Promise<SearchHit[]> {
  const page = await http.get<
    AdminPage<{
      id: number;
      receiptNumber: string | null;
      customerName: string | null;
      status: string;
    }>
  >('/admin/orders', { params: { search: term, countryId, page: 1, pageSize: LIMIT }, signal });
  return page.items.map((o) => ({
    kind: 'order',
    id: o.id,
    title: `Order #${o.id}`,
    detail: [o.receiptNumber, o.customerName, o.status].filter(Boolean).join(' · ') || null,
    to: `/orders/${o.id}`,
  }));
}

export async function searchBrands(
  term: string,
  countryId: number | undefined,
  signal?: AbortSignal,
): Promise<SearchHit[]> {
  const page = await http.get<AdminPage<{ id: number; name: string; isActive: boolean }>>(
    '/admin/brands',
    { params: { search: term, countryId, page: 1, pageSize: LIMIT }, signal },
  );
  return page.items.map((b) => ({
    kind: 'brand',
    id: b.id,
    title: b.name,
    detail: b.isActive ? null : 'Inactive',
    to: `/brands/${b.id}`,
  }));
}

export async function searchProviders(term: string, signal?: AbortSignal): Promise<SearchHit[]> {
  const page = await http.get<AdminPage<{ id: number; name: string; isActive: boolean }>>(
    '/admin/providers',
    { params: { search: term, page: 1, pageSize: LIMIT }, signal },
  );
  return page.items.map((p) => ({
    kind: 'provider',
    id: p.id,
    title: p.name,
    detail: p.isActive ? null : 'Inactive',
    to: `/providers/${p.id}`,
  }));
}
